import { useCallback, useEffect } from "react";
import {
  UserChatInterfacePermissions,
  UserRoles,
  useAuthStore,
} from "../../store/auth.store";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { getAuthLogoutUrl } from "@shared/apis/sidekick/api-factories";
import { cookieService } from "../services/cookie-service";
import { useGetUserProfile } from "../../queries/profile/useGetUserProfile";
import { useRefreshToken } from "../../queries/auth/useRefreshToken";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { SubscriptionStatus } from "@features/Subscription/subscription.types";
import {
  getLoginOptionById,
  LOGIN_OPTION_STORAGE_KEY,
  useLoginOptions,
} from "@features/Auth/login-options-form-helpers";
import { REGISTRATION_OPTION_STORAGE_KEY } from "@pages/registration/subscription-registration-helpers";
import { getFirstNameAndLastName } from "@shared/utils/formatters";
import { formatInternationalPhoneNumber } from "@shared/utils/form-converters";
import { INDIVIDUAL_PLAN_ID } from "@shared/utils/constants";
import {
  domainGroupEnum,
  getCurrentDomainGroup,
} from "@shared/utils/domain-group";
import { useOtherDomainGroupUrl } from "./useOtherDomainGroupUrl";
import { useOtherAppName } from "./useAppName";
import { useUIStore } from "@store/ui.store";
import { WrongBrandDialogContent } from "@shared/components/WrongBrandDialogContent";

export const useAuth = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authTokenStatus = useAuthStore((state) => state.authTokenStatus);
  const sessionTerminated = useAuthStore((state) => state.sessionTerminated);
  const isValidating = useAuthStore((state) => state.isValidatingCookies);
  const error = useAuthStore((state) => state.authError);
  const user = useAuthStore((state) => state.user);
  const authenticate = useAuthStore((state) => state.authenticate);
  const setAuthTokenStatus = useAuthStore((state) => state.setAuthTokenStatus);
  const userNotAuthenticated = useAuthStore(
    (state) => state.userNotAuthenticated,
  );
  const userSessionExpired = useAuthStore((state) => state.userSessionExpired);
  const userInvalidToken = useAuthStore((state) => state.userInvalidToken);
  const { options: availableLoginOptions } = useLoginOptions();
  const otherDomainLoginUrl = useOtherDomainGroupUrl("/login");
  const otherAppName = useOtherAppName();
  const confirmDialog = useUIStore((state) => state.confirmDialog);

  const syncAuthTokenStatus = useCallback(() => {
    const newAuthTokenStatus = cookieService.getAuthTokenStatus();
    setAuthTokenStatus(newAuthTokenStatus);

    if (newAuthTokenStatus === null) {
      cookieService.deleteAllCookies();
      userNotAuthenticated();
    }
  }, [setAuthTokenStatus, userNotAuthenticated]);

  // The account's plan belongs to the other brand — the backend rejects
  // login/refresh for this domain, or (below) the account already carries
  // the other brand's module. This session can never succeed here, but we
  // can't silently redirect: the real session cookies are httpOnly, so only
  // the backend's own /auth/logout can actually clear them, and that's a
  // full-page navigation away from here. So instead we ask the user to
  // confirm — no dismiss option, it reopens on every reload until they do —
  // then open the other brand's login in a new tab (needs a real click to
  // avoid being popup-blocked) and log this tab out for real.
  const redirectToCorrectBrand = useCallback(async () => {
    await confirmDialog({
      title: `Let's get you to ${otherAppName}`,
      content: (
        <WrongBrandDialogContent
          otherAppName={otherAppName}
          url={otherDomainLoginUrl}
        />
      ),
      confirmButtonText: `Continue to ${otherAppName}`,
      cancelButtonText: false,
    });

    window.open(otherDomainLoginUrl, "_blank", "noopener,noreferrer");
    window.location.replace(getAuthLogoutUrl());
  }, [confirmDialog, otherAppName, otherDomainLoginUrl]);

  const refreshQuery = useRefreshToken({
    enabled: !sessionTerminated,
    onSuccess: () => {
      syncAuthTokenStatus();
    },
    onSessionExpired: () => {
      cookieService.deleteAllCookies();
      userSessionExpired();
    },
    onWrongBrand: redirectToCorrectBrand,
    onUnexpectedError: () => {
      syncAuthTokenStatus();
    },
  });

  useEffect(() => {
    syncAuthTokenStatus();
  }, [syncAuthTokenStatus]);

  useEffect(() => {
    if (authTokenStatus === "EXPIRED" && refreshQuery.isError) {
      cookieService.deleteAllCookies();
      userNotAuthenticated();
    }
  }, [authTokenStatus, refreshQuery.isError, userNotAuthenticated]);

  const syncLoginAndRegistrationPreferencesStorage = (
    subscriptionPlanId: string,
  ) => {
    const loginOption = getLoginOptionById(
      availableLoginOptions,
      subscriptionPlanId,
    );
    if (loginOption) {
      localStorage.setItem(LOGIN_OPTION_STORAGE_KEY, loginOption.id);
    }
    if (sessionStorage.getItem(REGISTRATION_OPTION_STORAGE_KEY)) {
      sessionStorage.removeItem(REGISTRATION_OPTION_STORAGE_KEY);
    }
  };

  const userInfoQuery = useGetUserProfile({
    enabled: authTokenStatus === "VALID" && !refreshQuery.isFetching,
    onError: async (error) => {
      const apiError = await apiResponseErrorHandler(error);
      if (apiError.status === 401 || apiError.status === 403) {
        cookieService.deleteAllCookies();
        userInvalidToken();
      }
    },
    onSuccess: (data) => {
      // Existing session on the wrong brand's domain (e.g. the account's
      // plan moved to Radar after this session started here on Sidekick) —
      // catch it proactively instead of waiting for the next refresh's 403.
      // Keyed off the account's plan, not its modules: an MLS/Sidekick-plan
      // account can legitimately carry the Radar module as a bundled
      // feature without belonging on Radar's domain at all.
      if (
        data.subscriptionPlanId === INDIVIDUAL_PLAN_ID &&
        getCurrentDomainGroup() !== domainGroupEnum.RADAR
      ) {
        redirectToCorrectBrand();
        return;
      }

      const subscriptionPlanId = data.subscriptionPlanId as string;
      const subscriptionStatus = data.subscriptionStatus as SubscriptionStatus;
      const firstName = data.name?.split(" ")[0] ?? "";

      if (!isAuthenticated) {
        const nameParts = getFirstNameAndLastName(data.name);
        appEventEmitter.emit({
          eventType: "sidekick-auth-identify-user",
          trackingId: data.trackingId,
          firstName: nameParts.firstName,
          lastName: nameParts.lastName,
          mlsProviders: data.allowedMlsProviders.join(", "),
          subscriptionPlanId,
          roles: data.roles.join(", "),
        });
      }

      authenticate({
        id: data.id,
        name: data.name,
        firstName,
        email: data.email,
        timezone: data.timezone ?? "",
        countryState: data.countryState ?? "",
        country: data.country ?? "",
        phoneNumber: formatInternationalPhoneNumber(data.phoneNumber) ?? "",
        mlsId: data.mlsId ?? "",
        officeName: data.officeName ?? "",
        authTokenExp: cookieService.getCookie(
          "sidekick-authorization-token-exp",
        ),
        avatar: data.avatar || "",
        roles: data.roles as UserRoles[],
        allowedChatInterfaces:
          data.allowedChatInterfaces as UserChatInterfacePermissions[],
        allowedMlsProviders: data.allowedMlsProviders,
        enabledMlsProviders: data.enabledMlsProviders || [],
        allowedMlsProvidersLabels: data.allowedMlsProvidersLabels || [],
        trackingId: data.trackingId,
        subscriptionPlanId,
        subscriptionStatus,
        hash: data.hash,
        createdOn: new Date(data.createdOn),
        subscriptionPlanTier: data.subscriptionPlanTier || "basic",
        communicationPreferences: data.communicationPreferences,
        modules: data.modules || [],
      });

      syncLoginAndRegistrationPreferencesStorage(subscriptionPlanId);
    },
  });

  const userRoles = user?.roles || [];
  const isProfileNetworkError =
    !error && (userInfoQuery.error as Error | null)?.name === "FetchError";

  return {
    error: isProfileNetworkError
      ? {
          message: "Error fetching the data. Connection refused.",
          type: "NETWORK_ERROR",
        }
      : error,
    isValidatingCredentials:
      (isValidating || userInfoQuery.isLoading) && !userInfoQuery.isError,
    userRoles,
    isAdmin: userRoles.includes(UserRoles.Admin),
    isAuthenticated,
    user,
  };
};
